import axios from 'axios'
import { setupCache } from 'axios-cache-interceptor'
import qs from 'qs'

import Toast from '@/mixins/toast'
import { API_URL } from '@/utilities/index'
import { clearToken, getToken } from '@/utilities/token'

const abortObjects = []

const axiosInstance = setupCache(axios.create({
  paramsSerializer: (params) => { return qs.stringify(params, { encodeValuesOnly: true, arrayFormat: 'brackets' }) }
}))

axiosInstance.defaults.withCredentials = false
axiosInstance.defaults.headers.common['Accept'] = 'application/json'
axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'

class RestClient {
  constructor (requiresAuth = true) {
    this.requiresAuth = requiresAuth
    this.instance = axiosInstance

    if (requiresAuth) {
      this.setAuthorizationHeader();
    }
  }

  /**
   * Protected methods
   */
  async _getAll (url, options = {}) {
    let cursor = ''
    const resources = []
    do {
      const page = await this._getPage(url, cursor, options)
      if (page.items) {
        resources.push(...page.items)
        cursor = page.nextCursor
      } else {
        cursor = ''
      }
    } while (cursor)
    return resources
  }

  async _getPage (url, cursor = '', options = {}) {
    const response = await this.makeRequest('get', url, null, { params: this.sanitizedParams({ cursor: cursor, field: options.sortField || 'id', sort: options.sortOrder || 'asc', ...options.params }) }, options)
    return response ? { items: this.getResponseData(response), nextCursor: this.getNextCursor(response) } : {}
  }

  async _get (url, config = {}, options = {}) {
    const response = await this.makeRequest('get', url, null, config, options)
    return response ? this.getResponseData(response) : {}
  }

  async _post (url, resource, config = {}) {
    const response = await this.makeRequest('post', url, resource, config)
    return this.getResponseData(response)
  }

  async _put (url, resource, config = {}) {
    const response = await this.makeRequest('put', url, resource, config)
    return this.getResponseData(response)
  }

  async _delete (url, config = {}) {
    return await this.makeRequest('delete', url, null, config)
  }

  abort (abortGroup) {
    abortObjects.filter(abortObject => abortObject.group === abortGroup).forEach((abortObject) => {
      abortObject.controller.abort()
    })
  }

  baseUrl () {
    return API_URL()
  }

  sanitizedParams (params) {
    const sanitizedParams = {}
    Object.keys(params).forEach(key => {
      if (params[key]) {
        sanitizedParams[key] = params[key]
      }
    })
    return sanitizedParams
  }

  setAuthorizationHeader () {
    const token = getToken();

    if (token) {
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.instance.defaults.headers.common['Authorization'];
    }
  }

  async makeRequest (method, url, resource, config = {}, options = {}) {
    const abortObject = this.createAbortObject(options.abortGroup)
    const requestConfig = { ...this.baseConfig(), ...config, signal: abortObject.controller.signal }
    try {
      let response = null
      switch (method) {
        case 'get':
          response = await this.instance.get(url, requestConfig)
          break
        case 'post':
          response = await this.instance.post(url, resource, requestConfig)
          break
        case 'put':
          response = await this.instance.put(url, resource, requestConfig)
          break
        case 'delete':
          response = await this.instance.delete(url, requestConfig)
          break
        default:
          response = await this.instance.get(url, requestConfig)
          break
      }
      return response
    } catch (errorObj) {
      if (errorObj.code === 'ERR_CANCELED') {
        return null
      } else {
        this.handleError(errorObj)
        return errorObj.response
      }
    } finally {
      const abortIndex = abortObjects.findIndex(obj => obj === abortObject)
      abortObjects.splice(abortIndex, 1)
    }
  }

  createAbortObject (abortGroup) {
    const abortObject = { controller: new AbortController(), group: abortGroup || 'default' }
    abortObjects.push(abortObject)
    return abortObject
  }

  handleError (errorObj) {
    switch (errorObj.response?.status) {
      case 401:
        clearToken();
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          window.location.href = '/login';
        }
        break;
      default:
        Toast.methods.$toast(errorObj.message)
        break
    }
  }

  getResponseData (response) {
    if (response && response.data) {
      return response.data
    } else {
      return {}
    }
  }

  getNextCursor (response) {
    return response.headers['next-cursor']
  }

  baseConfig () {
    if (this.requiresAuth) {
      this.setAuthorizationHeader();
    }

    return {
      baseURL: this.baseUrl()
    }
  }
}

export default RestClient
